/**
 * <variant-dropdown> - the card flavour/variant dropdown.
 *
 * Port of live's VariantDropdown custom element (from the legacy JS
 * bundle), card path. Selecting an option morphs the card in
 * place: image, title + link, price (incl. sale / bulk-compare states), unit
 * price, customer quote, swatch, short name, and the quick-add button's
 * variant + availability state.
 *
 * Kept exactly as live, including the dataset STRING comparisons in the
 * price logic (`bulkComparePrice > price` compares strings when both are
 * data-attribute values - reproduced, not corrected).
 *
 * Two deliberate deltas, both documented in the piece report:
 * - live resolved the quick-add button with a selector that only matched the
 *   full-width button variant, so on icon-overlay cards a sold-out selection
 *   threw mid-handler and left the card half-updated. The lookup here matches
 *   both variants and null-guards.
 * - the quick-add is a real product form here (progressive enhancement), so
 *   the form's hidden variant input is updated along with live's scf_id
 *   input and button dataset.
 *
 * The ingredients-table path of live's element belongs to the PDP piece and
 * is not ported here.
 */

function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const moneyFormat = format || '€{{amount_with_comma_separator}}';

  function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    if (Number.isNaN(number) || number == null) return '0';
    const fixed = (number / 100.0).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  }

  let value = '';
  switch (moneyFormat.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }
  return moneyFormat.replace(placeholderRegex, value);
}

class VariantDropdown extends HTMLElement {
  constructor() {
    super();
    this._handleDocumentClick = this._handleDocumentClick.bind(this);
  }

  connectedCallback() {
    this.dropdownToggle = this.querySelector('.variant-dropdown');
    this.dropdownContent = this.querySelector('.dropdown-variant-selection');
    this.variantItems = this.querySelectorAll('.dropdown-variant-wrapper');
    this.colors = this.querySelectorAll('.variant-dropdown-color');
    this.closeIcon = this.querySelector('.dropdown-close-icon');
    this.card = this.closest('.card');
    if (this.card) {
      this.quickAddWrapper = this.card.querySelector('.quick-add-wrapper[data-quick-add]');
      this.quickAddButton = this.card.querySelector('[data-quick-add-submit]');
    }

    if (!this.dropdownToggle || !this.dropdownContent) return;

    this.dropdownToggle.addEventListener('click', () => this.toggleDropdown());
    this.variantItems.forEach((item) => {
      item.addEventListener('click', (event) => {
        this.handleItemClick(event);
        this.closeDropdown();
      });
    });
    if (this.closeIcon) {
      this.closeIcon.addEventListener('click', () => this.closeDropdown());
    }
  }

  handleItemClick(event) {
    if (!this.card) return;
    const variantWrapper = event.target.closest('.dropdown-variant-wrapper');
    if (!variantWrapper) return;

    const { variantId: id, productName: title, color, image } = variantWrapper.dataset;
    const { price, compareAtPrice, unitPrice, productReview, shortName, productUrl: url, available } = variantWrapper.dataset;
    let { bulkComparePrice } = variantWrapper.dataset;
    const moneyFormat = document.body.getAttribute('data-money-format');

    // quick-add state
    if (this.quickAddWrapper) {
      if (available === 'true') {
        this.quickAddWrapper.removeAttribute('disabled');
      } else if (available === 'false') {
        this.quickAddWrapper.setAttribute('disabled', '');
      }
      if (this.quickAddButton) {
        const label = available === 'true' ? this.getAttribute('btn-text-atc') : this.getAttribute('btn-text-sold-out');
        if (this.quickAddButton.querySelector('span:not(.sold-out-message)')) {
          const labelSpan = this.quickAddButton.querySelector('span:not(.sold-out-message)');
          if (!labelSpan.querySelector('img')) labelSpan.innerText = label;
        }
        this.quickAddButton.dataset.variantId = id;
        this.quickAddButton.toggleAttribute('disabled', available !== 'true');
      }
      const formVariantInput = this.quickAddWrapper.querySelector('form input[name="id"].product-variant-id');
      if (formVariantInput && id) formVariantInput.value = id;
    }

    // checkmark
    this.colors.forEach((swatch) => swatch.classList.remove('selected-variant'));
    variantWrapper.querySelector('.variant-dropdown-color').classList.add('selected-variant');

    if (id) {
      this.card.querySelector('.variant-dropdown').setAttribute('data-selected-variant-id', id);
      const scfIdElement = this.card.querySelector('.scf_id');
      if (scfIdElement) scfIdElement.setAttribute('value', id);
    }
    if (title) {
      this.card.querySelector('.card__title').innerText = title;
    }

    const priceRoot = this.card.querySelector('.price');
    if (priceRoot) {
      const regular = priceRoot.querySelector('.price__regular');
      const sale = priceRoot.querySelector('.price__sale');
      if (bulkComparePrice && bulkComparePrice > price) {
        bulkComparePrice = parseFloat(bulkComparePrice).toFixed(2);
        priceRoot.classList.add('price--on-sale');
        regular.style.display = 'none';
        sale.querySelector('.price-item--sale').innerText = formatMoney(price, moneyFormat);
        sale.querySelector('.price-item--regular').innerText = formatMoney(bulkComparePrice, moneyFormat);
        sale.style.display = 'block';
      } else if (bulkComparePrice && bulkComparePrice <= price) {
        priceRoot.classList.remove('price--on-sale');
        priceRoot.querySelector('.price-item').innerHTML = `${formatMoney(price, moneyFormat)}<span>*</span>`;
        sale.style.display = 'none';
        regular.style.display = 'block';
      } else if (price) {
        if (compareAtPrice && compareAtPrice > price) {
          priceRoot.classList.add('price--on-sale');
          regular.style.display = 'none';
          sale.querySelector('.price-item--sale').innerText = formatMoney(price, moneyFormat);
          sale.querySelector('.price-item--regular').innerText = formatMoney(compareAtPrice, moneyFormat);
          sale.style.display = 'block';
        } else {
          priceRoot.classList.remove('price--on-sale');
          priceRoot.querySelector('.price-item').innerHTML = `${formatMoney(price, moneyFormat)}<span>*</span>`;
          sale.style.display = 'none';
          regular.style.display = 'block';
        }
      }
    }

    const unitPriceTarget = this.card.querySelector('[data-unit-price]');
    if (unitPrice && unitPriceTarget) {
      unitPriceTarget.innerText = unitPrice;
    }
    if (url) {
      this.card.querySelector('.card__title').href = url;
    }
    if (productReview) {
      const quote = this.card.querySelector('.card-information');
      if (quote) quote.innerText = productReview;
    }
    if (color) {
      this.card.querySelector('.variant-dropdown-color').style.background = color;
    }
    if (shortName) {
      this.card.querySelector('.variant-dropdown-product').innerText = shortName;
    }
    if (image) {
      const cardImage = this.card.querySelector('.card__media img');
      if (cardImage) {
        cardImage.src = image;
        cardImage.srcset = image;
      }
    }
  }

  _handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.closeDropdown();
    }
  }

  toggleDropdown() {
    if (this.dropdownToggle.hasAttribute('open')) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    this.dropdownContent.style.display = 'block';
    this.dropdownToggle.setAttribute('open', '');
    document.addEventListener('click', this._handleDocumentClick, true);
  }

  closeDropdown() {
    this.dropdownContent.style.display = 'none';
    this.dropdownToggle.removeAttribute('open');
    document.removeEventListener('click', this._handleDocumentClick, true);
  }
}

if (!customElements.get('variant-dropdown')) {
  customElements.define('variant-dropdown', VariantDropdown);
}
